import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import Loader from '../Components/Loader';
import axiosInstance from '../utils/axiosInstance';
import { formatNumber } from '../utils/numberUtil';
import { isSameDay } from '../utils/dateUtil';
import { runSafely } from '../utils/runSafely';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';

interface UserInfoType {
    walletBalances: number;
    todayCommission: number;
    clickRemaining: number;
    userOrders: any[];
    orderCountPrompt?: number;
    orderCountMessage?: string;
    userProducts: {
        id: number;
        amount: number;
        productId: number;
        name: string;
        seller: string;
        img: string;
    }[]
}

const OrderPage: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfoType>();

    const [press, setPress] = useState(0);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [pendingOrder, setPendingOrder] = useState();
    const { t } = useTranslation();

    const pressOrder = async () => {
        // if (!userInfo?.userProducts?.length || !userInfo?.clickRemaining) {
        //     Swal.fire({
        //         title: 'Error!',
        //         text: 'Option Occupied, Please contact your instructor',
        //         icon: 'error',
        //     })
        //     getUserInfo();
        //     return;
        // }
        if (!userInfo?.userProducts?.length) {
            Swal.fire({
                title: 'Error!',
                text: 'Option Occupied, Please contact your instructor',
                icon: 'error',
            })
            runSafely('OrderPage.getUserInfo (retry)', getUserInfo, () => setLoading(false));
            return;
        }
        let chosenProduct = userInfo?.userProducts[0];
        // setLoading(true);
        if (press < 2) {
            setPress(press + 1);
            setTimeout(() => {
                setLoading(false);
            }, 1000)
        } else {
            // proceed
            let hasPendingOrder = await getPendingOrder();
            if (!hasPendingOrder) {
                const res = await axiosInstance.post("/protected/createOrder", {
                    productId: chosenProduct.productId,
                    amount: chosenProduct.amount,
                    id: chosenProduct.id,
                })
                if (res.status === 201) {
                    if (userInfo.userOrders.length === userInfo.orderCountPrompt && userInfo.orderCountMessage) {
                        Swal.fire({
                            text: userInfo.orderCountMessage,
                            icon: "warning",
                        }).then(() => {
                            navigate("/confirmation")
                        })
                    } else {
                        navigate("/confirmation")
                    }
                } else {
                    Swal.fire({
                        title: 'Error!',
                        text: 'An error occurred',
                        icon: 'error',
                    })
                }
            }
        }
    }

    const getUserInfo = async () => {
        setLoading(true);
        await getPendingOrder();
        const res = await axiosInstance.get("/protected/getUserInfo")
        const data = res.data.data;
        if (data) {
            const user = data.user;
            if (!user.isActive) {
                navigate("/login");
                return;
            }
            const userProducts = data.userProducts;
            const userOrders: any[] = data.userOrders;

            const todayOrders = userOrders.filter(e => isSameDay(new Date(), new Date(e.createdAt)));
            const todayCommission = todayOrders.reduce((total, e) => total + Number(e.comission), 0)

            setUserInfo({
                walletBalances: user.walletBalances,
                todayCommission: todayCommission,
                clickRemaining: user.clickRemaining,
                orderCountMessage: user.orderCountMessage,
                orderCountPrompt: user.orderCountPrompt,
                userOrders: userOrders,
                userProducts: userProducts?.map((e: any) => {
                    return {
                        id: e.id,
                        amount: e.amount,
                        productId: e.Product?.id,
                        name: e.Product?.name ?? "",
                        seller: e.Product?.seller ?? "",
                        img: process.env.REACT_APP_FOLDER_URL + "/" + (e.Product?.img ?? ""),
                    }
                }) || []
            })
        } else {
            navigate("/login")
        }
        setTimeout(() => {
            setLoading(false);
        }, 1500);

    }

    const getPendingOrder = async () => {
        const res = await axiosInstance.get("/protected/getPendingOrder")
        const data = res.data.data;
        if (data) {
            const pendingOrder = data.pendingOrder;
            setPendingOrder(pendingOrder);
            if (pendingOrder)
                return true;
            return false;
        } else {
            navigate("/login")
            return true;
        }
    }

    // Mount-only fetch; see AccountPage for why the dependency is omitted.
    useEffect(() => {
        setPress(0);
        runSafely('OrderPage.getUserInfo', getUserInfo, () => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Intentionally keyed on pendingOrder alone. `navigate` is stable in
    // react-router 7, so listing it would add noise without changing anything.
    useEffect(() => {
        if (pendingOrder) {
            navigate("/confirmation")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingOrder])

    /**
     * The three figures in the balance card's stat strip, in display order.
     * Data rather than two parallel hand-written rows, which is what kept the
     * old markup's labels and values in sync only by position.
     */
    const STATS = [
        {
            key: 'today',
            labelKey: 'today_reward',
            value: formatNumber(userInfo?.todayCommission ?? 0),
        },
        {
            key: 'completed',
            labelKey: 'completed',
            value: userInfo?.userOrders?.filter(e => e.orderStatus === "completed")?.length ?? 0,
        },
        {
            key: 'ongoing',
            labelKey: 'ongoing',
            value: userInfo?.userOrders?.filter(e => e.orderStatus === "pending")?.length ?? 0,
        },
    ];

    /** The two order steps, in order. Numbering is derived from the index. */
    const STEPS = ['receive_data_order', 'submit_merged_data'];

    return (
        <AppShell backdrop="/images/bonia/bonia-front-store.webp" backdropOpacity={0.85}>
            {loading && <Loader />}
            <Container>
                {/* Single focused action; capped narrower than Container's full width at lg
                    so the page doesn't sprawl into empty space on wide screens. */}
                <div className='px-5 pt-8 pb-10 flex flex-col gap-8 lg:max-w-[720px] lg:mx-auto lg:pt-12'>
                    {/* Balance card. Real border + shadow from the design tokens,
                        replacing the borderless white block with the inline
                        border-radius, and one consistent inset instead of the
                        old p-2/py-4 outer box with a p-4 wrapper inside it. */}
                    <div className='rounded-lg border border-solid border-brand-300 bg-white shadow-card p-5 lg:p-6'>
                        <div className='text-xs uppercase tracking-[0.16em] text-brand-600'>
                            {t("digital_balance")}
                        </div>
                        {/* Playfair on the figure, matching how every other page in
                            the app sets a currency amount. */}
                        <div className='mt-2 flex items-baseline gap-2'>
                            <span className='text-[15px] text-brand-600'>RM</span>
                            <span className='font-display text-[34px] leading-none text-brand-900'>
                                {formatNumber(userInfo?.walletBalances ?? 0)}
                            </span>
                        </div>

                        {/* `flex-1` on each label makes the labels absorb the row's
                            spare height, so the three values stay on one line
                            together even when one label wraps to two lines. */}
                        <div className='mt-5 grid grid-cols-3 rounded-md bg-accent-500/10 py-3'>
                            {STATS.map(({ key, labelKey, value }, i) => (
                                <div
                                    key={key}
                                    className={
                                        'flex flex-col items-center px-2 ' +
                                        (i > 0 ? 'border-0 border-l border-solid border-brand-300' : '')
                                    }
                                >
                                    <span className='flex flex-1 items-center text-center text-[10px] uppercase tracking-[0.1em] leading-tight text-brand-600'>
                                        {t(labelKey)}
                                    </span>
                                    <span className='mt-1.5 text-[16px] text-brand-900'>
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <Button
                            onClick={() => {
                                pressOrder();
                            }}
                            className={
                                'w-full mt-5 text-white bg-brand-800 uppercase tracking-[0.12em] ' +
                                'hover:!bg-accent-600 hover:!border-accent-600 ' +
                                'focus-visible:outline focus-visible:outline-2 ' +
                                'focus-visible:outline-offset-2 focus-visible:outline-accent-500'
                            }
                            size='large' type="primary"
                        >
                            {t("receive_order")}
                        </Button>
                    </div>

                    {/* Steps. A real <ol> — these are ordered instructions, and the
                        numbers now come from the list rather than being typed in. */}
                    <div>
                        <h2 className='m-0 font-display text-xl text-brand-900 pb-3 border-0 border-b border-solid border-brand-300'>
                            {t("steps")}
                        </h2>
                        <ol className='list-none m-0 p-0 mt-5 flex flex-col gap-4'>
                            {STEPS.map((labelKey, i) => (
                                <li key={labelKey} className='flex items-center gap-3'>
                                    {/* Fixed square box, so this is a circle. The old
                                        rounded-[100%] with py-1 px-2 rendered an oval. */}
                                    <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-800 text-[12px] text-white'>
                                        {i + 1}
                                    </span>
                                    <span className='text-sm text-brand-900'>{t(labelKey)}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </Container>
        </AppShell>
    );
}

export default OrderPage;